import { memo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, EyeOff, Play, PlayCircle, Sparkles, Video } from "lucide-react";
import type { RejiPlaylistItem } from "@wedding/shared";
import { cn } from "../../lib/utils";
import { cdnUrl } from "../../lib/cdn";
import { useLongPress } from "./use-long-press";
import { computeReorderIndex, type ReorderAction } from "./reorder";

interface RejiGridProps {
  items: RejiPlaylistItem[];
  currentMediaId: string | null;
  nextMediaId: string | null;
  busy: boolean;
  onGoto: (mediaId: string) => void;
  onHide: (mediaId: string) => void;
  onUnhide: (mediaId: string) => void;
  onSpotlight: (mediaId: string) => void;
  onMove: (mediaId: string, toIndex: number) => void;
}

/**
 * Media thumb: images via /cdn resize; videos as a muted metadata-only
 * <video> — an <img> cannot render a video URL (this was the "no video
 * preview" bug), while preload="metadata" paints the first frame cheaply.
 */
function ThumbMedia({ item, small }: { item: RejiPlaylistItem; small?: boolean }) {
  if (item.kind === "video") {
    return (
      <video
        src={item.url}
        preload="metadata"
        muted
        playsInline
        disablePictureInPicture
        tabIndex={-1}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
    );
  }
  return (
    <img
      src={cdnUrl(item.url, { w: small ? 64 : 480 })}
      alt=""
      loading="lazy"
      draggable={false}
      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
    />
  );
}

function Thumb({
  item,
  position,
  isCurrent,
  isNext,
  onTap,
  onLongPress,
  thumbRef,
}: {
  item: RejiPlaylistItem;
  /** 1-based position in the show order (grid numbering). */
  position: number;
  isCurrent: boolean;
  isNext: boolean;
  onTap: () => void;
  onLongPress: () => void;
  thumbRef?: (el: HTMLButtonElement | null) => void;
}) {
  const { t } = useTranslation();
  const { handlers, firedRef } = useLongPress(onLongPress);

  return (
    <button
      ref={thumbRef}
      {...handlers}
      onClick={() => {
        if (firedRef.current) return; // long-press already handled this gesture
        onTap();
      }}
      onContextMenu={(e) => e.preventDefault()}
      aria-label={item.hidden ? t("reji.unhide") : t("reji.nowPlaying")}
      className={cn(
        "relative aspect-square rounded-2xl overflow-hidden border transition-all duration-200 cursor-pointer select-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 active:scale-[0.97]",
        "lg:hover:scale-[1.02] lg:hover:shadow-lg lg:hover:shadow-black/40 hover:brightness-110",
        isCurrent ? "border-primary ring-2 ring-primary" : "border-white/10 hover:border-white/30",
        item.hidden && "opacity-40 grayscale",
      )}
    >
      <ThumbMedia item={item} />
      {/* Show-order number — the director's sequencing at a glance */}
      <span className="absolute top-1.5 left-1.5 rounded-full bg-black/55 text-white/85 font-mono text-[10px] tabular-nums px-1.5 py-0.5">
        {position}
      </span>
      {item.kind === "video" && (
        <span className="absolute top-1.5 right-1.5 rounded-full bg-black/55 p-1">
          <Video className="h-3 w-3 text-white/90" />
        </span>
      )}
      {isCurrent && (
        <span className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-full bg-primary text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
          <Play className="h-2.5 w-2.5" /> {t("reji.nowPlaying")}
        </span>
      )}
      {!isCurrent && isNext && !item.hidden && (
        <span className="absolute bottom-1.5 left-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
          {t("reji.nextUp")}
        </span>
      )}
      {item.hidden && (
        <span className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-full bg-black/60 text-white/85 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5">
          <EyeOff className="h-2.5 w-2.5" /> {t("reji.hidden")}
        </span>
      )}
    </button>
  );
}

/**
 * The Flow: every slide in show order. Tap → goto (all screens jump);
 * long-press → action sheet (Spotlight / Hide); hidden thumbs tap → restore.
 */
function RejiGridImpl({
  items,
  currentMediaId,
  nextMediaId,
  busy,
  onGoto,
  onHide,
  onUnhide,
  onSpotlight,
  onMove,
}: RejiGridProps) {
  const { t } = useTranslation();
  const [sheetFor, setSheetFor] = useState<RejiPlaylistItem | null>(null);
  const currentThumbRef = useRef<HTMLButtonElement | null>(null);

  const openSheet = (item: RejiPlaylistItem) => setSheetFor(item);

  // Semantic reorder: map the action to a toIndex (or null = disabled) and fire.
  const curIdx = currentMediaId ? items.findIndex((i) => i.id === currentMediaId) : -1;
  const reorder = (item: RejiPlaylistItem, action: ReorderAction) => {
    const fromIdx = items.findIndex((i) => i.id === item.id);
    const to = computeReorderIndex(action, fromIdx, curIdx, items.length);
    if (to === null) return;
    onMove(item.id, to);
    setSheetFor(null);
  };

  const visible = items.filter((i) => !i.hidden).length;
  const hiddenCount = items.length - visible;

  return (
    <div className="px-4 lg:px-6 pb-4 lg:pb-6">
      <div className="flex items-center justify-between gap-3 px-1 pb-2.5 lg:pt-4 lg:pb-3 sticky top-0 z-10 bg-background/85 backdrop-blur-md -mx-1 pt-1">
        <h2 className="text-white/85 text-sm font-semibold uppercase tracking-[0.22em]">{t("reji.flow")}</h2>
        <div className="flex items-center gap-3">
          <p className="text-white/40 text-xs tabular-nums">
            {visible}
            {hiddenCount > 0 && <span> · {hiddenCount} {t("reji.hidden").toLowerCase()}</span>}
          </p>
          {/* Long playlists: one tap back to the live slide */}
          {currentMediaId && items.length > 12 && (
            <button
              onClick={() =>
                currentThumbRef.current?.scrollIntoView({ block: "center", behavior: "smooth" })
              }
              className="flex items-center gap-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-[10px] font-bold uppercase tracking-wider px-2 py-1 hover:bg-primary/25 transition-colors cursor-pointer"
            >
              <Play className="h-2.5 w-2.5" /> {t("reji.nowPlaying")}
            </button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-white/40 text-sm px-1 py-6">{t("reji.emptyPlaylist")}</p>
      ) : (
        // auto-fill density: hundreds of items stay scannable — column count
        // grows with pane width (mobile ~3-4, desktop right pane 6-10).
        <div className="grid grid-cols-[repeat(auto-fill,minmax(6rem,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-2 lg:gap-3">
          {items.map((item, i) => (
            <Thumb
              key={item.id}
              item={item}
              position={i + 1}
              isCurrent={item.id === currentMediaId}
              isNext={item.id === nextMediaId}
              thumbRef={item.id === currentMediaId ? (el) => { currentThumbRef.current = el; } : undefined}
              onTap={() => {
                if (busy) return;
                // Hidden thumbs open the action sheet (with "Geri Getir") instead
                // of instantly un-hiding — a stray tap shouldn't resurrect them.
                if (item.hidden) openSheet(item);
                else onGoto(item.id);
              }}
              onLongPress={() => openSheet(item)}
            />
          ))}
        </div>
      )}

      {/* Long-press action sheet */}
      {sheetFor &&
        createPortal(
          <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/60" onClick={() => setSheetFor(null)} />
            <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-[#1b1817]/95 backdrop-blur-xl border-t border-white/10 p-4 safe-bottom">
              <div className="flex items-center gap-3 pb-3 border-b border-white/10">
                <span className="relative size-12 rounded-xl overflow-hidden border border-white/10 shrink-0">
                  <ThumbMedia item={sheetFor} small />
                </span>
                <p className="text-white/80 text-sm truncate">
                  {sheetFor.uploader_name || t("reji.flow")}
                </p>
              </div>
              <div className="pt-2 space-y-1">
                {/* Director sequencing: one-tap semantic reorder (no drag) */}
                {(() => {
                  const fromIdx = items.findIndex((i) => i.id === sheetFor.id);
                  const can = (a: ReorderAction) => computeReorderIndex(a, fromIdx, curIdx, items.length) !== null;
                  const chip =
                    "flex-1 flex flex-col items-center gap-1 rounded-xl px-2 py-2.5 text-white/85 text-xs font-medium " +
                    "bg-white/5 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer " +
                    "disabled:opacity-35 disabled:cursor-not-allowed";
                  return (
                    <>
                      <div className="flex items-center gap-2 pt-1">
                        <button type="button" onClick={() => reorder(sheetFor, "front")} disabled={!can("front")} className={chip}>
                          <ChevronsUp className="h-4 w-4 text-primary" /> {t("reji.orderFirst")}
                        </button>
                        <button type="button" onClick={() => reorder(sheetFor, "next")} disabled={!can("next")} className={chip}>
                          <PlayCircle className="h-4 w-4 text-primary" /> {t("reji.moveNext")}
                        </button>
                        <button type="button" onClick={() => reorder(sheetFor, "end")} disabled={!can("end")} className={chip}>
                          <ChevronsDown className="h-4 w-4 text-primary" /> {t("reji.moveEnd")}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => reorder(sheetFor, "up")} disabled={!can("up")} className={chip}>
                          <ArrowUp className="h-4 w-4" /> {t("reji.nudgeUp")}
                        </button>
                        <button type="button" onClick={() => reorder(sheetFor, "down")} disabled={!can("down")} className={chip}>
                          <ArrowDown className="h-4 w-4" /> {t("reji.nudgeDown")}
                        </button>
                      </div>
                    </>
                  );
                })()}
                {!sheetFor.hidden && (
                  <button
                    onClick={() => { onSpotlight(sheetFor.id); setSheetFor(null); }}
                    className="w-full flex items-center gap-3 rounded-xl px-3 py-3.5 text-white hover:bg-white/10 transition-colors cursor-pointer text-left"
                  >
                    <Sparkles className="h-5 w-5 text-primary" />
                    <span className="font-medium">{t("reji.spotlight")}</span>
                    <span className="ml-auto text-white/40 text-xs">{t("reji.nextUp")}</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    if (sheetFor.hidden) onUnhide(sheetFor.id);
                    else onHide(sheetFor.id);
                    setSheetFor(null);
                  }}
                  className="w-full flex items-center gap-3 rounded-xl px-3 py-3.5 text-white hover:bg-white/10 transition-colors cursor-pointer text-left"
                >
                  <EyeOff className="h-5 w-5 text-amber-400" />
                  <span className="font-medium">{sheetFor.hidden ? t("reji.unhide") : t("reji.hide")}</span>
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export const RejiGrid = memo(RejiGridImpl);
